import { Helmet } from 'react-helmet-async';
import { getCalculatorSchema } from '../config/features';
import seoContent, { landingFaqs, buildFaqSchema } from '../config/seoContent';
import { BRAND, BRAND_APP } from '../config/brand';

const BASE_URL = 'https://data-calc.com';
const SITE_NAME = BRAND_APP;

// Site-level schema, emitted on the homepage only. Lives here (not in
// public/index.html) so each prerendered route carries only its own schema.
const SITE_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://conform.tools/#organization',
    name: BRAND,
    legalName: `${BRAND} LLC`,
    url: 'https://conform.tools',
    sameAs: ['https://www.instagram.com/conform.tools'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: `${BRAND_APP} — Video Space Calculator`,
    alternateName: ['DataCalc', 'Video Space Calculator', 'Video File Size Calculator', 'Video Storage Calculator'],
    description:
      'Free video space calculator and file size estimator for video professionals. Calculate video storage needs, camera recording times, aspect ratios, bitrates, timecode math, transfer times, and more.',
    url: BASE_URL,
    applicationCategory: 'UtilityApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    author: { '@id': 'https://conform.tools/#organization' },
    publisher: { '@id': 'https://conform.tools/#organization' },
    featureList: [
      'Video Space Calculator',
      'Video File Size Calculator',
      'Camera Storage Calculator',
      'Video Recording Time Calculator',
      'Aspect Ratio Calculator',
      'Timecode Calculator',
      'Transfer Time Calculator',
      'Motion Picture Film Calculator',
      'Video Bitrate Calculator',
      'Framing Chart Generator',
      'Dailies Calculator',
    ],
    audience: {
      '@type': 'Audience',
      audienceType: 'Video Professionals, Filmmakers, DITs, Editors, Colorists, Cinematographers, Producers',
    },
  },
];

/**
 * SEOHead - Dynamic per-page meta tags for SEO.
 * Renders <title>, meta description, canonical, Open Graph, Twitter Card,
 * and JSON-LD structured data for each page. react-snap bakes these into
 * each route's static HTML at build time.
 */
const SEOHead = ({ pageInfo, calculatorKey }) => {
  if (!pageInfo) return null;

  const title = pageInfo.metaTitle || `${pageInfo.title} | ${SITE_NAME}`;
  const description = pageInfo.metaDescription || pageInfo.description;

  // Unknown routes: noindex, no canonical, no social/schema markup.
  if (pageInfo.noindex) {
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  const canonicalUrl = `${BASE_URL}${pageInfo.path}`;
  const ogImage = pageInfo.ogImage
    ? `${BASE_URL}${pageInfo.ogImage}`
    : `${BASE_URL}/og-image.png`;

  const isLanding = pageInfo.path === '/';
  const calculatorSchema = calculatorKey ? getCalculatorSchema(calculatorKey) : null;
  const faqs = isLanding ? landingFaqs : seoContent[calculatorKey]?.faqs;
  const faqSchema = faqs && faqs.length > 0 ? buildFaqSchema(faqs) : null;

  // Breadcrumbs tell Google the site hierarchy, which is what turns the raw
  // URL in a result into a "Home > Dailies Calculator" trail.
  const breadcrumbSchema = isLanding ? null : {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
      { '@type': 'ListItem', position: 2, name: pageInfo.title, item: canonicalUrl },
    ],
  };

  const schemaNodes = [
    ...(isLanding ? SITE_SCHEMA : []),
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
    ...(calculatorSchema ? [calculatorSchema] : []),
    ...(faqSchema ? [faqSchema] : []),
  ];

  return (
    <Helmet>
      {/* Primary */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {schemaNodes.length > 0 && (
        <script type="application/ld+json">{JSON.stringify(schemaNodes)}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
